import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Input from 'src/@vymo/ui/atoms/input';
import { addMilliseconds, isAfter, isBefore } from 'date-fns';
import { ReactComponent as SwapRight } from 'src/assets/icons/between.svg';
import DatePicker from '../datePicker';
import {
  ensureEndDateAfterStart,
  isSameDateRangeValue,
  isSameDateValue,
} from '../queries';
import {
  getTimeStepMinutesFromDuration,
  getTimeStepMsFromDuration,
} from './queries';
import type { DateTimeRangePickerProps } from './types';
import styles from './index.module.scss';

/* eslint-disable complexity */
function RangePicker({
  value,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  onChange = _.noop,
  format,
  duration = [],
  placeholderText = { default: '' },
  viewMode = false,
  showTime = false,
  'data-test-id': dataTestId,
  ...rangePickerProps
}: DateTimeRangePickerProps) {
  const effectiveMin = min ?? Number.NEGATIVE_INFINITY;
  const effectiveMax = max ?? Number.POSITIVE_INFINITY;
  const [openEndCalendar, setOpenEndCalendar] = useState(false);
  const [openStartCalendar, setOpenStartCalendar] = useState(false);

  const [dateArray, setDateArray] = useState(value);

  const [startDate = null, endDate = null] = dateArray ?? [];

  const timeStepMs = useMemo(
    () => getTimeStepMsFromDuration(duration),
    [duration],
  );
  const timeStepMinutes = useMemo(
    () => getTimeStepMinutesFromDuration(duration),
    [duration],
  );

  useEffect(() => {
    const [start = null, end = null] = value ?? [];
    const normalized: [Date | null, Date | null] =
      start && end && !isAfter(end, start)
        ? [start, ensureEndDateAfterStart(start, end, timeStepMs)]
        : value ?? [null, null];

    if (!isSameDateRangeValue(dateArray, normalized)) {
      setDateArray(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, timeStepMs]);

  const onStartDateChange = useCallback(
    (date, event) => {
      if (!date) {
        // @ts-ignore
        onChange([], event);
        setDateArray([null, null]);
        return;
      }

      let nextEnd = endDate;
      if (!endDate || !isAfter(endDate, date)) {
        nextEnd = addMilliseconds(date, timeStepMs);
      }

      // @ts-ignore
      onChange([date, nextEnd], event);
      setDateArray([date, nextEnd]);
    },
    [endDate, onChange, timeStepMs],
  );

  const onEndDateChange = useCallback(
    (date, event) => {
      if (!date) {
        // @ts-ignore
        onChange([], event);
        setDateArray([null, null]);
        return;
      }

      if (!startDate) {
        // @ts-ignore
        onChange([startDate, date], event);
        setDateArray([startDate, date]);
        return;
      }

      const nextEnd = ensureEndDateAfterStart(startDate, date, timeStepMs);

      // @ts-ignore
      onChange([startDate, nextEnd], event);
      setDateArray([startDate, nextEnd]);
    },
    [onChange, startDate, timeStepMs],
  );

  const shouldOpenEndAfterStartClose = useCallback(() => {
    if (!startDate) {
      return false;
    }
    if (!endDate) {
      return true;
    }
    if (isBefore(endDate, startDate)) {
      return true;
    }
    if (isSameDateValue(endDate, startDate)) {
      return true;
    }
    // showTime: start change auto-fills end to start + step; still open end for confirmation
    if (showTime && endDate) {
      const endOffsetMs = endDate.getTime() - startDate.getTime();
      return endOffsetMs > 0 && endOffsetMs <= timeStepMs;
    }
    return false;
  }, [endDate, showTime, startDate, timeStepMs]);

  const onStartDateCalendarOpen = useCallback(() => {
    setOpenEndCalendar(false);
    setOpenStartCalendar(true);
  }, []);

  const onStartDateCalendarClose = useCallback(() => {
    setOpenStartCalendar(false);
    if (shouldOpenEndAfterStartClose()) {
      window.setTimeout(() => setOpenEndCalendar(true), 0);
    }
  }, [shouldOpenEndAfterStartClose]);

  const onEndDateCalendarOpen = useCallback(() => {
    setOpenStartCalendar(false);
    setOpenEndCalendar(true);
  }, []);

  const onEndDateCalendarClose = useCallback(() => {
    setOpenEndCalendar(false);
    if (
      (endDate && !startDate) ||
      (endDate && startDate && isBefore(endDate, startDate))
    ) {
      window.setTimeout(() => setOpenStartCalendar(true), 0);
    }
  }, [endDate, startDate]);

  const timeStep = useMemo(
    () => ({ stepMinutes: timeStepMinutes }),
    [timeStepMinutes],
  );

  const endMinTimeFloor = useMemo(
    () =>
      startDate && showTime ? addMilliseconds(startDate, timeStepMs) : null,
    [showTime, startDate, timeStepMs],
  );

  return (
    <div
      className={!viewMode ? styles.rangePicker : ''}
      data-test-id={dataTestId}
    >
      <DatePicker
        selectsStart
        open={openStartCalendar}
        onChange={onStartDateChange}
        value={startDate}
        startDate={startDate}
        endDate={endDate}
        min={effectiveMin}
        max={effectiveMax}
        format={format}
        timeStep={timeStep}
        onCalendarOpen={onStartDateCalendarOpen}
        onCalendarClose={onStartDateCalendarClose}
        calendarPhase="start"
        placeholderText={placeholderText?.start || placeholderText.default}
        data-test-id={dataTestId ? `${dataTestId}-start` : undefined}
        customInput={
          <Input
            isOnChangeArgFirstEvent
            hideValidation
            onChange={onStartDateChange}
            variant="block"
            showDisabledIcon={rangePickerProps.showDisabledIcon}
            data-test-id={dataTestId ? `${dataTestId}-start` : undefined}
            onTouchStart={onStartDateCalendarOpen}
            onClick={onStartDateCalendarOpen}
          />
        }
        wrapperClassName={styles.rangePicker__start}
        viewMode={viewMode}
        showTime={showTime}
        {...rangePickerProps}
      />
      <SwapRight />
      <DatePicker
        selectsEnd
        showIcon={false}
        showTime={showTime}
        open={openEndCalendar}
        onChange={onEndDateChange}
        value={endDate}
        startDate={startDate}
        endDate={endDate}
        min={effectiveMin}
        max={effectiveMax}
        minMaxRelativeDate={startDate}
        minTimeFloor={endMinTimeFloor}
        format={format}
        timeStep={timeStep}
        onCalendarOpen={onEndDateCalendarOpen}
        onCalendarClose={onEndDateCalendarClose}
        calendarPhase="end"
        placeholderText={placeholderText?.end || placeholderText.default}
        data-test-id={dataTestId ? `${dataTestId}-end` : undefined}
        customInput={
          <Input
            isOnChangeArgFirstEvent
            clearInputIcon={false}
            hideValidation
            onChange={onEndDateChange}
            variant="block"
            showDisabledIcon={rangePickerProps.showDisabledIcon}
            data-test-id={dataTestId ? `${dataTestId}-end` : undefined}
            onTouchStart={onEndDateCalendarOpen}
            onClick={onEndDateCalendarOpen}
          />
        }
        wrapperClassName={styles.rangePicker__end}
        viewMode={viewMode}
        {...rangePickerProps}
      />
    </div>
  );
}

export default RangePicker;
